import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminSports, useUpdateSportConfig, useUpdateLocationSportConfig, useToggleSportAtLocation } from '@/api/hooks/useAdminSports';
import { useSupportedLocales } from '@/api/hooks/useSupportedLocales';
import { pickLocaleValue } from '@/lib/i18n-pick';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Pencil, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import type { AdminSport } from '@/api/types';

const PRICING_MODELS = ['PER_UNIT_PER_HOUR', 'PER_PERSON_PER_HOUR'];

const PRICING_MODEL_LABELS: Record<string, string> = {
  PER_UNIT_PER_HOUR: 'Per Unit / Per Hour',
  PER_PERSON_PER_HOUR: 'Per Person / Per Hour',
};

type SportsManagementProps = {
  locationId?: string;
};

export default function SportsManagement({ locationId }: SportsManagementProps) {
  const { t, i18n } = useTranslation();
  const { data: sports, isLoading } = useAdminSports();
  const { showFrTab } = useSupportedLocales();
  const updateConfig = useUpdateSportConfig();
  const updateLocConfig = useUpdateLocationSportConfig();
  const toggleSport = useToggleSportAtLocation();
  const [editingSport, setEditingSport] = useState<AdminSport | null>(null);
  const [activeLocaleTab, setActiveLocaleTab] = useState<string>('en-CA');

  // Edit form state. PR V.c added formNameEn / formNameFr to support
  // the tabbed-by-locale name entry. Non-localizable fields stay as
  // plain useState rather than migrating to react-hook-form -- the
  // current form has 6 such fields and only `name` is localizable, so
  // the full migration's value vs. risk ratio didn't justify expanding
  // V.c's diff. Tracked as a separate follow-up if the form grows
  // additional localizable fields later.
  const [formNameEn, setFormNameEn] = useState('');
  const [formNameFr, setFormNameFr] = useState('');
  const [originalNameFr, setOriginalNameFr] = useState('');
  const [formMinPlayers, setFormMinPlayers] = useState(1);
  const [formMaxPlayers, setFormMaxPlayers] = useState(6);
  const [formDefaultDuration, setFormDefaultDuration] = useState(60);
  const [formIncrement, setFormIncrement] = useState(15);
  const [formBuffer, setFormBuffer] = useState(0);
  const [formPricingModel, setFormPricingModel] = useState('PER_UNIT_PER_HOUR');

  const openEdit = (sport: AdminSport) => {
    setEditingSport(sport);
    setActiveLocaleTab('en-CA');
    // Always preload BOTH locale values from the per-locale map admin
    // endpoint exposes (PR V.b). Empty string for missing fr-CA -- the
    // tabbed form's FR input renders as a blank entry prompt.
    setFormNameEn(sport.name['en-CA'] ?? '');
    setFormNameFr(sport.name['fr-CA'] ?? '');
    setOriginalNameFr(sport.name['fr-CA'] ?? '');
    setFormMinPlayers(sport.minPlayers);
    setFormMaxPlayers(sport.maxPlayers);
    setFormDefaultDuration(sport.defaultDurationMinutes);
    setFormIncrement(sport.sessionIncrementMinutes);
    setFormBuffer((sport.bookingConfig?.bufferMinutes as number) ?? 0);
    setFormPricingModel(sport.pricingModel);
  };

  // Per-locale dirty tracking for the dirty-dot indicator on inactive
  // tab triggers. en-CA dirty when formNameEn differs from the loaded
  // value; fr-CA dirty when formNameFr differs from its loaded value.
  const enDirty = editingSport != null && formNameEn !== (editingSport.name['en-CA'] ?? '');
  const frDirty = formNameFr !== originalNameFr;

  // Build the name map to send to the backend. Only include locales the
  // operator actually touched -- locales absent from the request map
  // are preserved (never deleted) by the backend per V.c's contract.
  const buildNamePayload = (): Record<string, string> => {
    const out: Record<string, string> = { 'en-CA': formNameEn };
    if (showFrTab && formNameFr !== originalNameFr) {
      out['fr-CA'] = formNameFr;
    }
    return out;
  };

  const handleSaveConfig = async () => {
    if (!editingSport) return;
    try {
      if (!locationId) {
        // Global edit
        await updateConfig.mutateAsync({
          sportCode: editingSport.code,
          name: buildNamePayload(),
          minPlayers: formMinPlayers,
          maxPlayers: formMaxPlayers,
          defaultDurationMinutes: formDefaultDuration,
          sessionIncrementMinutes: formIncrement,
          pricingModel: formPricingModel,
          bookingConfig: { bufferMinutes: formBuffer },
        });
      } else {
        // Per-location override — Sprint 7 Evening 7 added pricingModel
        // to the override payload so operators set their own without
        // mutating the global sports row (which would leak across
        // tenants).
        await updateLocConfig.mutateAsync({
          locationId,
          sportCode: editingSport.code,
          config: {
            defaultDurationMinutes: formDefaultDuration,
            bookingIncrement: formIncrement,
            bufferMinutes: formBuffer,
            pricingModel: formPricingModel,
          },
        });
      }
      toast.success(t('admin.sport_config_saved'));
      setEditingSport(null);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleToggle = async (locationId: string, sportCode: string, enabled: boolean) => {
    try {
      await toggleSport.mutateAsync({ locationId, sportCode, enabled });
      toast.success(enabled ? t('admin.sport_enabled') : t('admin.sport_disabled'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  if (isLoading) {
    return <Skeleton className="h-64" />;
  }

  return (
    <>
      <Card className="border-l-4 border-l-emerald-500">
        <CardContent className="p-6 space-y-4">
          <h2 className="text-sm font-semibold">{t('admin.sports_management')}</h2>
          <Separator />
          <p className="text-xs text-muted-foreground">{t('admin.sports_hint')}</p>

          {sports && sports
            .filter((sport) => {
              if (!locationId) return true;
              // When a location is selected, only show sports that have units there
              return sport.locationStatuses.some((l) => l.locationId === locationId && l.unitCount > 0);
            })
            .map((sport) => {
              const visibleLocations = locationId
                ? sport.locationStatuses.filter((l) => l.locationId === locationId)
                : sport.locationStatuses;

              return (
            <div key={sport.code} className="rounded-md border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{sport.icon}</span>
                  <div>
                    <span className="font-medium text-sm">{t(`sport_${sport.code}`, { defaultValue: pickLocaleValue(sport.name, i18n.language) })}</span>
                    {/* PR V.c: "not yet translated" badge for bilingual
                         tenants on sports missing a FR translation. Only
                         renders when supportedLocales includes fr-CA;
                         English-only tenants never see this. */}
                    {showFrTab && !sport.name['fr-CA'] && (
                      <Badge variant="outline" className="ml-2 border-amber-200 bg-amber-50 text-amber-700 text-[10px]">
                        {t('admin.not_yet_translated')}
                      </Badge>
                    )}
                    <span className="ml-2 text-xs text-muted-foreground">
                      {sport.pricingModel} · {sport.defaultDurationMinutes}min · {sport.sessionIncrementMinutes}min increment
                    </span>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => openEdit(sport)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>

              {/* Per-location toggles */}
              <div className="flex flex-wrap gap-3">
                {visibleLocations.map((loc) => (
                  <div key={loc.locationId} className="flex items-center gap-2 rounded-md border px-3 py-1.5">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs">{loc.locationName}</span>
                    <span className="text-xs text-muted-foreground">({loc.unitCount})</span>
                    <Switch
                      checked={loc.enabled}
                      onCheckedChange={(checked) => handleToggle(loc.locationId, sport.code, checked)}
                      size="sm"
                    />
                  </div>
                ))}
              </div>
            </div>
              );
            })}
        </CardContent>
      </Card>

      {/* Edit Sport Config Dialog */}
      <Dialog open={!!editingSport} onOpenChange={(open) => { if (!open) setEditingSport(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSport?.icon} {t('admin.edit_sport_config')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {locationId && (
              <p className="text-xs text-muted-foreground">{t('admin.location_override_hint')}</p>
            )}

            {/* Name + players only for global scope */}
            {!locationId && (
              <>
                <div>
                  <Label>{t('admin.col_name')}</Label>
                  {/* PR V.c: tabbed-by-locale name entry. EN tab is
                       always present (canonical, required); FR tab
                       conditionally shown when the tenant opts into FR
                       via operational_policies.supportedLocales (V90).
                       Atomic save persists both locales in one PUT;
                       absent or unchanged locales are NOT deleted by
                       the backend (operator work-in-progress is safe).
                       Dirty-dot indicator on the inactive tab trigger
                       prevents the "I typed FR but saved without
                       realising the dirty tab" failure mode. */}
                  {showFrTab ? (
                    <Tabs value={activeLocaleTab} onValueChange={(v) => setActiveLocaleTab((v as string) ?? 'en-CA')} className="mt-1">
                      <TabsList>
                        <TabsTrigger value="en-CA">
                          {t('admin.locale_english')}
                          {enDirty && activeLocaleTab !== 'en-CA' && (
                            <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#C2713A]" aria-label={t('admin.locale_unsaved_changes')} />
                          )}
                        </TabsTrigger>
                        <TabsTrigger value="fr-CA">
                          {t('admin.locale_french')}
                          {frDirty && activeLocaleTab !== 'fr-CA' && (
                            <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#C2713A]" aria-label={t('admin.locale_unsaved_changes')} />
                          )}
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="en-CA">
                        <Input value={formNameEn} onChange={(e) => setFormNameEn(e.target.value)} className="mt-1" />
                      </TabsContent>
                      <TabsContent value="fr-CA">
                        <Input value={formNameFr} onChange={(e) => setFormNameFr(e.target.value)} placeholder={t('admin.locale_french_placeholder')} className="mt-1" />
                      </TabsContent>
                    </Tabs>
                  ) : (
                    <Input value={formNameEn} onChange={(e) => setFormNameEn(e.target.value)} className="mt-1" />
                  )}
                </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>{t('admin.min_players')}</Label>
                <Input type="number" min={1} value={formMinPlayers} onChange={(e) => setFormMinPlayers(Number(e.target.value))} className="mt-1" />
              </div>
              <div>
                <Label>{t('admin.max_players')}</Label>
                <Input type="number" min={1} value={formMaxPlayers} onChange={(e) => setFormMaxPlayers(Number(e.target.value))} className="mt-1" />
              </div>
            </div>
              </>
            )}

            {/* Duration/increment/buffer — available for both global and location scope */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>{t('admin.default_duration')}</Label>
                <div className="flex items-center gap-1.5 mt-1">
                  <Input type="number" min={15} step={15} value={formDefaultDuration} onChange={(e) => setFormDefaultDuration(Number(e.target.value))} />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">min</span>
                </div>
              </div>
              <div>
                <Label>{t('admin.max_duration')}</Label>
                <div className="flex items-center gap-1.5 mt-1">
                  <Input type="number" min={15} step={15} value={formDefaultDuration * 4} readOnly />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">min</span>
                </div>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>{t('admin.booking_increment')}</Label>
                <div className="flex items-center gap-1.5 mt-1">
                  <Input type="number" min={5} step={5} value={formIncrement} onChange={(e) => setFormIncrement(Number(e.target.value))} />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">min</span>
                </div>
              </div>
              <div>
                <Label>{t('admin.buffer_between_sessions')}</Label>
                <div className="flex items-center gap-1.5 mt-1">
                  <Input type="number" min={0} step={5} value={formBuffer} onChange={(e) => setFormBuffer(Number(e.target.value))} />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">min</span>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Label>{t('admin.pricing_model')}</Label>
                <span
                  className={
                    'rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ' +
                    (locationId
                      ? 'border-amber-200 bg-amber-50 text-amber-700'
                      : 'border-neutral-200 bg-neutral-100 text-neutral-600')
                  }
                >
                  {locationId ? 'Location override' : 'Global default'}
                </span>
              </div>
              <Select value={formPricingModel} onValueChange={(v) => setFormPricingModel(v ?? 'PER_UNIT_PER_HOUR')}>
                <SelectTrigger className="mt-1">
                  <span className="text-sm">{PRICING_MODEL_LABELS[formPricingModel] ?? formPricingModel}</span>
                </SelectTrigger>
                <SelectContent>
                  {PRICING_MODELS.map((m) => (
                    <SelectItem key={m} value={m}>{PRICING_MODEL_LABELS[m] ?? m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {locationId && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Only affects bookings at this location. The global default is untouched.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSport(null)}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveConfig} disabled={updateConfig.isPending || updateLocConfig.isPending}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
